import Image from 'next/image';
import styled, { css } from 'styled-components';

import IconLocationPinMono from '@/components/atom/icon/IconLocationPinMono';
import LocalOffer from '@/components/atom/icon/hospitals/influenza/LocalOffer';
import theme from '@/styles/theme';
import { Nullable } from '@/types/common';
import { DiagnosisState } from '@/types/hospital';
import {
  formatKilometerByMeter,
  getCurrencyFormat,
  getDiagnosisStateText,
} from '@/utils/format';

interface Props {
  name: string;
  distance?: Nullable<number>;
  address: string;
  badges: string[];
  isBookable?: boolean;
  representativeDepartmentName?: string;
  imageUrl?: Nullable<string>;
  hospitalPid?: string;
  diagnosisPrice?: number;
  badgeLabelList?: string[];
  status: DiagnosisState;
}
const Hospital = ({
  name,
  distance,
  address,
  badges,
  isBookable = false,
  diagnosisPrice,
  imageUrl,
  badgeLabelList,
  status,
}: Props) => {
  const visibleBadgeList = badges.slice(0, 3);
  const hasMoreBadge = badges.length > visibleBadgeList.length;
  const moreBadgeLength = badges.length - visibleBadgeList.length;

  return (
    <Wrapper>
      <TopWrapper>
        <HospitalWrapper>
          <DiagnosisStatus type={status} />
          <TitleRow>
            <HospitalName>{name}</HospitalName>
          </TitleRow>
          <InfoWrapper>
            {!!distance && (
              <>
                <IconLocationPinMono fill={theme.color.gray300} />
                <Distance>{formatKilometerByMeter(distance)}</Distance>
              </>
            )}
            <Address>{address}</Address>
          </InfoWrapper>
          <PriceWrapper>
            {!!diagnosisPrice && (
              <Price>{getCurrencyFormat(diagnosisPrice)}원</Price>
            )}
            {!!badgeLabelList && badgeLabelList?.length > 0 && (
              <PriceBadgeWrapper>
                <LocalOffer />
                <PriceBadge>{badgeLabelList?.join('')}</PriceBadge>
              </PriceBadgeWrapper>
            )}
          </PriceWrapper>
        </HospitalWrapper>
        {imageUrl && (
          <ImageWrapper>
            <HospitalImage
              src={imageUrl}
              width={64}
              height={64}
              alt={name}
              loading="lazy"
            />
          </ImageWrapper>
        )}
      </TopWrapper>
      <BadgeWrapper hasItem={badges.length > 0}>
        {isBookable ? (
          <BookableBadge key={`badge-${name}-bookable`}>간편예약</BookableBadge>
        ) : (
          <UnBookableBadge key={`badge-${name}-${name}-unbookable`}>
            전화예약
          </UnBookableBadge>
        )}
        {visibleBadgeList.map((badge, i) => (
          <Badge key={`badge-${name}-${badge}-${i}`}>{badge}</Badge>
        ))}
        {hasMoreBadge && (
          <Badge key={`badge-${name}-+`}>+{moreBadgeLength}</Badge>
        )}
      </BadgeWrapper>
      <BorderLine />
    </Wrapper>
  );
};

export default Hospital;

const DiagnosisStatus = (props: { type: DiagnosisState }) => {
  const text = getDiagnosisStateText(props.type);
  switch (props.type) {
    case DiagnosisState.On:
      return (
        <StatusWrapper>
          <StatusOn />
          <StatusText type={props.type}>{text}</StatusText>
        </StatusWrapper>
      );
    case DiagnosisState.Off:
      return (
        <StatusWrapper>
          <StatusDisabledText type={props.type}>{text}</StatusDisabledText>
        </StatusWrapper>
      );
    case DiagnosisState.Lunch:
      return (
        <StatusWrapper>
          <StatusText type={props.type}>{text}</StatusText>
        </StatusWrapper>
      );
    case DiagnosisState.DayOff:
      return (
        <StatusWrapper>
          <StatusText type={props.type}>{text}</StatusText>
        </StatusWrapper>
      );
    case DiagnosisState.Unknown:
      return null;
  }
};

const Wrapper = styled.li`
  padding: 20px 12px 0;
  display: flex;
  flex-direction: column;
`;

const HospitalName = styled.div`
  ${({ theme }) => `
          ${theme.typography.UI15SB}
          color: ${theme.color.gray600};
      `}
`;

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HospitalWrapper = styled.div`
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  & > h3 {
    ${({ theme }) => `
          ${theme.typography.UI17SB}
          color: ${theme.color.gray600};
      `}
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: break-all;
    margin-bottom: 8px;
  }
`;

const TitleRow = styled.div``;

const InfoWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  align-items: center;
`;

const Distance = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI13M}
    color: ${theme.color.gray600};
  `}
  margin-left: 4px;
  margin-right: 6px;
`;

const Address = styled.span`
  ${({ theme }) => `
        ${theme.typography.UI13M}
        color: ${theme.color.gray300};
    `}
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  word-break: break-all;
`;

const BadgeWrapper = styled.div<{ hasItem: boolean }>`
  display: ${({ hasItem }) => (hasItem ? 'block' : 'none')};
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-top: 12px;
  ${({ theme }) => `
      ${theme.typography.UI12M}
      color: ${theme.color.gray300};
      `}
`;

const Badge = styled.p`
  ${({ theme }) => `
      ${theme.typography.UI12M}
      color: ${theme.color.gray300};
      background: ${theme.color.gray30};
      `}
  display: inline-block;
  padding: 1px 4px;
  border-radius: 4px;
  height: 20px;
  margin-right: 4px;
`;

const BookableBadge = styled(Badge)`
  ${({ theme }) => `
      color: ${theme.color.primary700};
      background: ${theme.color.primary100};
      `}
`;
const UnBookableBadge = styled(Badge)`
  ${({ theme }) => `
      color: ${theme.color.gray500};
      background: ${theme.color.gray50};

      `}
`;

const PriceBadgeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  margin-top: 6px;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  word-break: break-all;
`;

const Price = styled.div`
  ${({ theme }) => `
${theme.typography.UI18SB}
color: ${theme.color.gray700};
margin-right: 6px;
`}
`;

const PriceBadge = styled.div`
  ${({ theme }) => `
${theme.typography.UI12SB}
color: ${theme.color.primary700};
`}
`;

const ImageWrapper = styled.div`
  position: relative;
  max-width: 64px;
  max-height: 64px;
  width: 100%;
  height: 100%;
  margin-left: 16px;
`;

const HospitalImage = styled(Image)`
  border-radius: 8px;
  object-fit: cover;
  padding: 12px 0;
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 18px;
  margin-bottom: 6px;
`;

const StatusOn = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.color.green500};
  margin-right: 4px;
`;

const StatusText = styled.span<{ type: DiagnosisState }>`
  ${({ theme, type }) => {
    switch (type) {
      case DiagnosisState.On: {
        return css`
          ${theme.typography.UI12R}
          color: ${theme.color.green700};
        `;
      }
      case DiagnosisState.Lunch: {
        return css`
          ${theme.typography.UI12R}
          color: ${theme.color.gray300};
        `;
      }
      case DiagnosisState.Off: {
        return css`
          ${theme.typography.UI12R}
          color: ${theme.color.gray300};
        `;
      }
      case DiagnosisState.DayOff: {
        return css`
          ${theme.typography.UI12R}
          color: ${theme.color.gray300};
        `;
      }
      case DiagnosisState.Unknown: {
        return '';
      }
    }
  }}
`;

const StatusDisabledText = styled(StatusText)`
  ${({ theme }) => `
        color: ${theme.color.gray200};
    `}
`;

const BorderLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.color.gray40};
  margin-top: 20px;
`;
